import styled from '@emotion/styled';
import { Button } from '@mui/material';
import { useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useSearchParams } from 'react-router-dom';
import routes from '../../config/routes';
import {
  monthClosingFindRequested,
  monthClosingLoadingSelector,
  monthClosingFilterCountrySelector,
  monthClosingChangeFilterCountry,
  monthClosingChangeFilterAccountingMonth,
  monthClosingFilterAccountingMonthSelector,
  masterDataSelector,
} from '../../state';
import { LoadingButton } from '../buttons';
import { MonthClosingFilters } from './MonthClosingFilters';

const ActionContainer = styled.div`
  display: flex;
  width: max-content;
  margin-left: auto;
  justify-content: right;

  > button:first-of-type {
    margin-right: ${props => props.theme.spacing(2)};
  }

  ${props => props.theme.breakpoints.down('md')} {
    margin-left: auto;
    margin-right: auto;
    justify-content: center;
  }
`;

const ButtonApply = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const loading = useSelector(monthClosingLoadingSelector);
  const hasCountry = Boolean(useSelector(monthClosingFilterCountrySelector));
  const accountingMonthValue = useSelector(monthClosingFilterAccountingMonthSelector);

  const handleClickApply = useCallback(() => {
    if (accountingMonthValue?.accept) {
      const currentDate = accountingMonthValue.accept;
      const lastDayOfMonth = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0);
      dispatch(monthClosingChangeFilterAccountingMonth(lastDayOfMonth));
    }

    dispatch(monthClosingFindRequested());
  }, [dispatch, accountingMonthValue]);

  const applyDisabled = !hasCountry;

  return (
    <LoadingButton
      color="primary"
      variant="contained"
      onClick={handleClickApply}
      loading={loading}
      disabled={applyDisabled}
    >
      {t('navigation.buttons.apply')}
    </LoadingButton>
  );
};

const ButtonCloseMonth = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const path = `${routes.protectedNestedRoutes.monthClosing.children.create.path}`;
  const handleClick = useCallback(() => navigate(path), [navigate, path]);

  return (
    <Button color="primary" variant="contained" onClick={handleClick}>
      {t('monthClosing.labels.closeMonth')}
    </Button>
  );
};

const MonthClosingSearch = () => {
  const dispatch = useDispatch();
  const [searchParams] = useSearchParams();
  const masterData = useSelector(masterDataSelector);
  const hasCountry = Boolean(useSelector(monthClosingFilterCountrySelector));
  const applyDisabled = !hasCountry;
  const countryRequiredIncomplete = applyDisabled;

  useEffect(() => {
    const countryAcronym = searchParams.get('countryAcronym');
    const accountingMonth = searchParams.get('accountingMonth');

    if (!countryAcronym && !accountingMonth) return;

    let shouldSearch = false;

    if (countryAcronym && masterData.data?.countries) {
      const countryObj = masterData.data.countries.find(c => c.countryCode === countryAcronym);
      if (countryObj) {
        dispatch(monthClosingChangeFilterCountry(countryAcronym));
        shouldSearch = true;
      }
    }

    if (accountingMonth) {
      const [y, m] = accountingMonth.split('-').map(Number);
      const lastDay = new Date(y, m, 0);
      dispatch(monthClosingChangeFilterAccountingMonth(lastDay));
      shouldSearch = true;
    }

    if (shouldSearch && countryAcronym && accountingMonth) {
      dispatch(monthClosingFindRequested());
    }
  }, [searchParams, dispatch, masterData.data]);

  const actions = (
    <ActionContainer data-tour="process-actions">
      <ButtonCloseMonth />
      <ButtonApply />
    </ActionContainer>
  );
  return (
    <MonthClosingFilters
      actions={actions}
      applyDisabled={applyDisabled}
      countryRequiredIncomplete={countryRequiredIncomplete}
    />
  );
};

export default MonthClosingSearch;
